import type { PersonalBoardColumn } from '@tasks/contracts';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { CATEGORY_COLOR_VAR } from '../../lib/types';
import { statusCategoryLabels } from '../../i18n/enums';
import { IssueCardView } from '../issue/IssueCardView';
import styles from './PersonalBoardPage.module.css';

const CATEGORY_TONE = {
  todo: 'todo',
  in_progress: 'progress',
  done: 'done',
} as const;

export type PersonalBoardColumnProps = {
  readonly column: PersonalBoardColumn;
  readonly onOpenIssue: (key: string) => void;
  readonly onShowMore: () => void;
  readonly canLoadMore: boolean;
};

/** Колонка персональной доски: категория статуса и карточки из разных проектов. */
export function PersonalBoardColumnView({
  column,
  onOpenIssue,
  onShowMore,
  canLoadMore,
}: PersonalBoardColumnProps) {
  const title = statusCategoryLabels[column.category];
  const hidden = column.count - column.issues.length;

  return (
    <section
      className={styles['column']}
      data-testid={`personal-column-${column.category}`}
      aria-label={`Колонка ${title}`}
    >
      <header className={styles['columnHeader']}>
        <span
          aria-hidden
          className={styles['stripe']}
          style={{ background: CATEGORY_COLOR_VAR[column.category] }}
        />
        <h2 className={styles['columnTitle']}>{title}</h2>
        <span className={styles['columnCount']}>{column.count}</span>
      </header>

      {column.issues.length === 0 ? (
        <p className={styles['columnEmpty']}>Нет задач</p>
      ) : (
        <div className={styles['list']} data-testid={`personal-list-${column.category}`}>
          {column.issues.map((card) => (
            <div key={card.id} className={styles['item']}>
              <IssueCardView
                issue={card}
                onOpen={onOpenIssue}
                extra={
                  <>
                    <Badge tone={CATEGORY_TONE[card.status.category]}>{card.status.name}</Badge>
                    {card.project.isArchived ? <Badge tone="warning">архив</Badge> : null}
                  </>
                }
              />
            </div>
          ))}
        </div>
      )}

      {hidden > 0 ? (
        <footer className={styles['footer']}>
          <span>
            Показаны первые {column.issues.length} из {column.count}
          </span>
          {canLoadMore ? (
            <Button size="sm" variant="ghost" onClick={onShowMore}>
              Показать ещё
            </Button>
          ) : null}
        </footer>
      ) : null}
    </section>
  );
}
